import React, { useEffect, useState, useContext } from "react";
import ReactDOM from "react-dom";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Link,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Paper,
  Chip,
  Menu,
  MenuItem,
  Autocomplete,
  Popover,
  List,
  ListItem,
  ListItemText,
  ClickAwayListener
} from "@mui/material";
import { makeStyles } from "@material-ui/core/styles";
import {
  Close as CloseIcon,
  Create as CreateIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Wc as GenderIcon,
  Work as WorkIcon,
  Badge as CpfCnpjIcon,
  Home as AddressIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Info as InfoIcon,
  Notes as NotesIcon,
  ContactPhone as ContactIcon,
  Add as AddIcon,
  AccountBalance as WalletIcon,
  Lock as LockIcon,
  Warning as WarningIcon
} from "@mui/icons-material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";


import AccountBoxIcon from "@mui/icons-material/AccountBox";
import LanguageIcon from "@mui/icons-material/Language";

import ContactModal from "../ContactModal";
import { i18n } from "../../translate/i18n";
import { ContactNotes } from "../ContactNotes";
import { TagsKanbanContainer } from "../TagsKanbanContainer";
import MarkdownWrapper from "../MarkdownWrapper";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import formatSerializedId from "../../utils/formatSerializedId";
import isEqual from "lodash/isEqual"; // no topo do arquivo se ainda não tiver


const drawerWidth = 400;

const useStyles = makeStyles(theme => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0
  },
  drawerPaper: {
    width: drawerWidth,
    display: "flex",
    flexDirection: "column",
    borderRadius: 12
  },
  content: {
    flex: 1,
    overflowY: "auto",
    backgroundColor: theme.palette.inputBackground,
    ...theme.scrollbarStyles
  }
}));

const ContactDrawer = ({ open, handleDrawerClose, contact, ticket, loading }) => {
  const classes = useStyles();
  const [modalOpen, setModalOpen] = useState(false);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [hideNum, setHideNum] = useState(false);
  const [fullContact, setFullContact] = useState(contact);
  const [extraInfoModalOpen, setExtraInfoModalOpen] = useState(false);
  const [newExtraInfo, setNewExtraInfo] = useState({ name: "", value: "" });
  const [selectedExtraInfoIndex, setSelectedExtraInfoIndex] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [extraInfoToDelete, setExtraInfoToDelete] = useState(null);
  const [users, setUsers] = useState([]);
  const [walletAnchorEl, setWalletAnchorEl] = useState(null);
  const [confirmWalletChangeOpen, setConfirmWalletChangeOpen] = useState(false);
  const [pendingWalletChange, setPendingWalletChange] = useState(null);
  const { get } = useCompanySettings();
  const { user } = useContext(AuthContext);
  const [manualTicketCreationEnabled, setManualTicketCreationEnabled] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const lgpdHideNumber = await get({ column: "lgpdHideNumber" });
      if (lgpdHideNumber === "enabled") setHideNum(true);

      try {
        const manual = await get({ column: "manualTicketCreation" });
        setManualTicketCreationEnabled(String(manual?.manualTicketCreation || manual || "disabled") === "enabled");
      } catch (e) {
        setManualTicketCreationEnabled(false);
      }
    };
    fetchData();
  }, [get]);

  useEffect(() => {
    const loadContact = async () => {
      if (!contact?.id) return;
      try {
        const { data } = await api.get(`/contacts/${contact.id}`);
        setFullContact(data);
      } catch (err) {
        toastError(err);
      }
    };
    loadContact();
  }, [contact?.id]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/list");
        setUsers(data || []);
      } catch (err) {
        toastError(err);
      }
    };
    fetchUsers();
  }, []);

  const handleUpdateContact = async () => {
    if (!contact?.id) return;
    try {
      const { data } = await api.get(`/contacts/${contact.id}`);
      if (!isEqual(fullContact, data)) {
        setFullContact(data);
      }
    } catch (err) {
      toastError(err);
    }
  };

  const handleWalletChange = async (userId) => {
    try {
      const wallets = userId ? [{ id: userId }] : [];
      await api.put(`/contacts/${fullContact.id}`, {
        wallets: wallets
      });
      
      setFullContact(prev => ({
        ...prev,
        wallets: userId ? users.filter(user => user.id === userId) : []
      }));
      
      setWalletAnchorEl(null);
      setConfirmWalletChangeOpen(false);
      setPendingWalletChange(null);
    } catch (err) {
      toastError(err);
    }
  };

  const handleWalletChangeRequest = (userId) => {
    const newUser = users.find(u => u.id === userId);
    setPendingWalletChange({
      userId,
      newUser,
      currentUser: fullContact?.wallets?.[0]
    });
    setConfirmWalletChangeOpen(true);
    setWalletAnchorEl(null);
  };

  const handleConfirmWalletChange = () => {
    if (pendingWalletChange) {
      handleWalletChange(pendingWalletChange.userId);
    }
  };

  const handleCancelWalletChange = () => {
    setConfirmWalletChangeOpen(false);
    setPendingWalletChange(null);
  };
  

  const handleAddExtraInfo = async () => {
    if (!newExtraInfo.name.trim() || !newExtraInfo.value.trim()) {
      toastError("Preencha os dois campos para adicionar uma informação extra.");
      return;
    }
  
    try {
      const updatedExtraInfo = [...(fullContact.extraInfo || [])];
  
      if (selectedExtraInfoIndex !== null) {
        updatedExtraInfo[selectedExtraInfoIndex] = { ...newExtraInfo };
      } else {
        updatedExtraInfo.push({ ...newExtraInfo });
      }
  
      await api.put(`/contacts/${fullContact.id}`, {
        extraInfo: updatedExtraInfo
      });
  
      setFullContact(prev => ({
        ...prev,
        extraInfo: updatedExtraInfo
      }));
  
      setExtraInfoModalOpen(false);
      setNewExtraInfo({ name: "", value: "" });
      setSelectedExtraInfoIndex(null);
    } catch (err) {
      toastError(err);
    }
  };
  

  const handleDeleteExtraInfo = async () => {
    if (!extraInfoToDelete || !fullContact?.id) return;
  
    try {
      const updatedExtraInfo = (fullContact.extraInfo || []).filter(
        info => info.id !== extraInfoToDelete.id
      );
  
      await api.put(`/contacts/${fullContact.id}`, {
        extraInfo: updatedExtraInfo
      });
  
      setFullContact(prev => ({
        ...prev,
        extraInfo: updatedExtraInfo
      }));
  
      setConfirmDeleteOpen(false);
      setExtraInfoToDelete(null);
    } catch (err) {
      toastError(err);
    }
  };
  




  const renderAddress = () => {
    const parts = [
      fullContact.street,
      fullContact.numberHouse,
      fullContact.complement,
      fullContact.neighborhood,
      fullContact.city,
      fullContact.state,
      fullContact.zipCode
    ].filter(Boolean);
    return parts.join(", ");
  };

  const WalletContainer = () => {
    const selectedWallet = fullContact?.wallets?.[0];
    const [chipRef, setChipRef] = useState(null);
    
    // Função melhorada para verificar permissões
    const canEditWallet = () => {
      // Admins e super podem sempre editar
      if (user.profile === "admin" || user.profile === "super") {
        return true;
      }
      
      // O próprio usuário que está vinculado à carteira pode editar
      const currentWalletUserId = selectedWallet?.id;
      if (currentWalletUserId && currentWalletUserId === user.id) {
        return true;
      }
      
      return false;
    };

    const hasEditPermission = canEditWallet();
    
    // Tooltip dinâmico baseado na permissão
    const getTooltipText = () => {
      if (hasEditPermission) {
        return "Clique para alterar o atendente vinculado";
      }
      
      const isOwnWallet = selectedWallet?.id === user.id;
      if (isOwnWallet) {
        return "Apenas você ou um administrador pode alterar esta carteira";
      }
      
      return "Apenas o usuário proprietário ou um administrador pode alterar esta carteira";
    };
    
    const handleChipClick = (e) => {
      e.stopPropagation();
      if (hasEditPermission) {
        setWalletAnchorEl(walletAnchorEl ? null : e.currentTarget);
      }
    };
    
    return (
      <>
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            backgroundColor: "#f5f5f5",
            p: 1.2,
            borderRadius: 2,
            mb: 2,
            gap: 1,
            width: "100%",
            position: "relative",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            <WalletIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: "middle" }} />
            Carteira de Clientes:
          </Typography>

          <Tooltip title={getTooltipText()}>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                position: "relative",
                gap: 0.5
              }}
            >
              <Chip
                ref={setChipRef}
                label={selectedWallet?.name || "Nenhum atendente"}
                onClick={handleChipClick}
                sx={{
                  backgroundColor: selectedWallet ? "#4caf50" : "#ddd",
                  color: "#fff",
                  fontWeight: "bold",
                  fontSize: "0.75rem",
                  px: 1,
                  flex: 1,
                  borderRadius: 3,
                  textAlign: "center",
                  justifyContent: "center",
                  cursor: hasEditPermission ? "pointer" : "not-allowed",
                  opacity: hasEditPermission ? 1 : 0.7,
                  "&:hover": {
                    opacity: hasEditPermission ? 0.9 : 0.7
                  },
                  ...(selectedWallet && hasEditPermission && {
                    boxShadow: "0 2px 4px rgba(76, 175, 80, 0.3)"
                  })
                }}
              />
              
              {/* Ícone de cadeado quando não tem permissão */}
              {!hasEditPermission && (
                <LockIcon 
                  fontSize="small" 
                  sx={{ 
                    color: "#666",
                    opacity: 0.7
                  }} 
                />
              )}
            </Box>
          </Tooltip>
        </Paper>

        {/* Dropdown menu apenas para usuários com permissão */}
        {hasEditPermission && Boolean(walletAnchorEl) && chipRef && ReactDOM.createPortal(
          <ClickAwayListener onClickAway={() => setWalletAnchorEl(null)}>
            <Box
              sx={{
                position: "fixed",
                top: chipRef.getBoundingClientRect().bottom + 4,
                left: chipRef.getBoundingClientRect().left,
                right: "auto",
                zIndex: 9999,
                backgroundColor: "white",
                borderRadius: 2,
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                minWidth: chipRef.getBoundingClientRect().width,
                maxWidth: "300px",
                maxHeight: "200px",
                overflowY: "auto",
                border: "1px solid rgba(0,0,0,0.1)",
              }}
            >
              <List dense sx={{ py: 0 }}>
                <ListItem 
                  button 
                  onClick={() => handleWalletChangeRequest(null)}
                  sx={{
                    py: 1,
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                >
                  <ListItemText 
                    primary="Nenhum atendente" 
                    primaryTypographyProps={{
                      fontSize: "0.875rem",
                      fontWeight: 500,
                    }}
                  />
                </ListItem>
                {users.map(user => (
                  <ListItem
                    key={user.id}
                    button
                    onClick={() => handleWalletChangeRequest(user.id)}
                    sx={{
                      py: 1,
                      "&:hover": {
                        backgroundColor: "#f0f0f0",
                      },
                    }}
                  >
                    <ListItemText 
                      primary={user.name} 
                      primaryTypographyProps={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </ClickAwayListener>,
          document.body
        )}
      </>
    );
  };

  const formattedPhone = hideNum && user.profile === "user"
    ? formatSerializedId(fullContact.number).slice(0, -6) + "**-**" + fullContact.number.slice(-2)
    : formatSerializedId(fullContact.number);

  const renderItem = (Icon, label, value, isLink = false) => {
    if (!value) return null;
    return (
      <Typography variant="body2" sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
        <Icon fontSize="small" sx={{ mr: 1 }} />
        {isLink ? (
          <Link
            href={`${label === "E-mail" ? "mailto:" : ""}${value}`}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
          >
            {value}
          </Link>
        ) : (
          value
        )}
      </Typography>
    );
  };


  if (loading || !fullContact) return null;

  return (
    <Drawer
      className={classes.drawer}
      variant="persistent"
      anchor="right"
      open={open}
      PaperProps={{ style: { position: "absolute" } }}
      BackdropProps={{ style: { position: "absolute" } }}
      ModalProps={{
        container: document.getElementById("drawer-container"),
        style: { position: "absolute" }
      }}
      classes={{ paper: classes.drawerPaper }}
    >
      <Box className={classes.content} sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            position: "relative",
            pb: 2
          }}
        >
          <IconButton
            onClick={handleDrawerClose}
            sx={{ position: "absolute", top: 8, right: 8 }}
            aria-label="Fechar"
          >
            <CloseIcon />
          </IconButton>

          {fullContact?.urlPicture ? (
            <img
              src={fullContact.urlPicture}
              alt={`Foto de ${fullContact.name}`}
              style={{
                width: 120,
                height: 120,
                borderRadius: "50%",
                objectFit: "cover",
                cursor: "pointer",
                userSelect: "none"
              }}
              onClick={() => setOpenImageDialog(true)}
              onContextMenu={e => e.preventDefault()}
            />
          ) : (
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: "50%",
                backgroundColor: "#ccc",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.25rem",
                fontWeight: 500
              }}
            >
              {fullContact.name?.[0]?.toUpperCase() || "?"}
            </Box>
          )}

          <Dialog
            open={openImageDialog}
            onClose={() => setOpenImageDialog(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{ style: { backgroundColor: "transparent", boxShadow: "none" } }}
          >
            <DialogContent
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
                padding: 0
              }}
              onClick={() => setOpenImageDialog(false)}
            >
              <IconButton
                onClick={() => setOpenImageDialog(false)}
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  color: "white"
                }}
              >
                <CloseIcon />
              </IconButton>
              <img
                src={fullContact.urlPicture}
                alt={fullContact.name}
                style={{
                  maxWidth: "100%",
                  maxHeight: "90vh",
                  borderRadius: 12,
                  pointerEvents: "none",
                  userSelect: "none"
                }}
                onContextMenu={e => e.preventDefault()}
              />
            </DialogContent>
          </Dialog>

          <Typography variant="h6" sx={{ mt: 2, display: "flex", alignItems: "center" }}>
            {fullContact.name || "Sem Nome"}
            <Tooltip title={i18n.t("contactDrawer.buttons.edit")}>
              <IconButton size="small" onClick={() => setModalOpen(true)}>
                <CreateIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Typography>
        </Box>

        {!manualTicketCreationEnabled && (
          <Box sx={{ mb: 2 }}>
            <TagsKanbanContainer ticket={ticket} />
          </Box>
        )}

        <Box sx={{ mb: 2 }}>
          <WalletContainer />
        </Box>

        <Divider sx={{ my: 2, width: "85%", mx: "auto", borderColor: "rgba(0, 0, 0, 0.1)" }} />

        {/* CONTATO */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: "center", mb: 1 }}>
            <ContactIcon fontSize="small" sx={{ mr: 1, verticalAlign: "middle" }} />
            Contato
          </Typography>
          {renderItem(PhoneIcon, "Telefone", formattedPhone)}
          {renderItem(EmailIcon, "E-mail", fullContact.email, true)}
        </Box>

        <Divider sx={{ my: 2, width: "85%", mx: "auto", borderColor: "rgba(0, 0, 0, 0.1)" }} />

        {/* DADOS PESSOAIS */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, textAlign: "center", display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}>
            <AccountBoxIcon fontSize="small" />
            Dados pessoais
          </Typography>

          {!fullContact.birthDate &&
            !fullContact.gender &&
            !fullContact.profession &&
            !fullContact.cpfCnpj &&
            !renderAddress() ? (
            <Typography variant="body2" sx={{ textAlign: "center", color: "text.secondary" }}>
              Nenhum dado informado. Edite o contato para inserir os dados pessoais.
            </Typography>
          ) : (
            <>
              {fullContact.birthDate && (
                <Typography variant="body2"><strong>Data de Nascimento:</strong> {new Date(fullContact.birthDate).toLocaleDateString()}</Typography>
              )}
              {fullContact.gender && (
                <Typography variant="body2"><strong>Sexo:</strong> {fullContact.gender === "M" ? "Masculino" : fullContact.gender === "F" ? "Feminino" : "Outro"}</Typography>
              )}
              {fullContact.profession && (
                <Typography variant="body2"><strong>Profissão:</strong> {fullContact.profession}</Typography>
              )}
              {fullContact.cpfCnpj && (
                <Typography variant="body2"><strong>CPF/CNPJ:</strong> {fullContact.cpfCnpj}</Typography>
              )}
              {renderAddress() && (
                <Typography variant="body2"><strong>Endereço:</strong> {renderAddress()}</Typography>
              )}
            </>
          )}
        </Box>

        <Divider sx={{ my: 2, width: "85%", mx: "auto", borderColor: "rgba(0, 0, 0, 0.1)" }} />

        {/* REDES SOCIAIS */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, textAlign: "center", display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}>
            <LanguageIcon fontSize="small" />
            Redes Sociais
          </Typography>

          {!fullContact.facebook &&
            !fullContact.instagram &&
            !fullContact.twitter &&
            !fullContact.linkedin ? (
            <Typography variant="body2" sx={{ textAlign: "center", color: "text.secondary" }}>
              Nenhuma rede social informada. Edite o contato para inserir os links sociais.
            </Typography>
          ) : (
            <>
              {fullContact.facebook && (
                <Typography variant="body2"><strong>Facebook:</strong> {fullContact.facebook}</Typography>
              )}
              {fullContact.instagram && (
                <Typography variant="body2"><strong>Instagram:</strong> {fullContact.instagram}</Typography>
              )}
              {fullContact.twitter && (
                <Typography variant="body2"><strong>Twitter/X:</strong> {fullContact.twitter}</Typography>
              )}
              {fullContact.linkedin && (
                <Typography variant="body2"><strong>LinkedIn:</strong> {fullContact.linkedin}</Typography>
              )}
            </>
          )}
        </Box>

        <Divider sx={{ my: 2, width: "85%", mx: "auto", borderColor: "rgba(0, 0, 0, 0.1)" }} />

        {/* NOTAS */}
        <Box sx={{ mb: 2 }}>
          <ContactNotes ticket={ticket} reverseOrder />
        </Box>

        <Divider sx={{ my: 2, width: "85%", mx: "auto", borderColor: "rgba(0, 0, 0, 0.1)" }} />

        {/* INFORMAÇÕES EXTRAS */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
              mb: 2
            }}
          >
            <InfoIcon sx={{ fontSize: 20, color: "primary.main" }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {i18n.t("contactDrawer.extraInfo") || "Informações Extras"}
            </Typography>
            <Tooltip title="Adicionar Informação Extra">
              <IconButton
                size="small"
                onClick={() => setExtraInfoModalOpen(true)}
                sx={{ color: "primary.main", p: 0.5 }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {fullContact?.extraInfo?.map((info, index) => (
            <Box
              key={info.id}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                p: 1.5,
                mb: 1.5,
                boxShadow: 1,
                backgroundColor: "background.paper",
                position: "relative"
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {info.name}
              </Typography>
              <Typography variant="body2" component="div" sx={{ color: "text.secondary" }}>
                <MarkdownWrapper>{info.value}</MarkdownWrapper>
              </Typography>
              <IconButton
                size="small"
                sx={{ position: "absolute", top: 4, right: 36 }}
                onClick={() => {
                  setNewExtraInfo({ name: info.name, value: info.value });
                  setSelectedExtraInfoIndex(index);
                  setExtraInfoModalOpen(true);
                }}
              >
                <CreateIcon fontSize="small" />
              </IconButton>

              <IconButton
                size="small"
                sx={{ position: "absolute", top: 4, right: 4 }}
                onClick={() => {
                  setExtraInfoToDelete(info);
                  setConfirmDeleteOpen(true);
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>

          ))}


        </Box>

      </Box>

      <ContactModal
  open={modalOpen}
  contactId={fullContact.id}
  onClose={(updatedContact) => {
    setModalOpen(false);
    if (updatedContact) {
      setFullContact(updatedContact);
    } else {
      handleUpdateContact();
    }
  }}
/>

      <Dialog open={extraInfoModalOpen} onClose={() => setExtraInfoModalOpen(false)}>
        <DialogTitle>Adicionar Informação Extra</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Título"
            fullWidth
            margin="dense"
            value={newExtraInfo.name}
            onChange={(e) => setNewExtraInfo({ ...newExtraInfo, name: e.target.value })}
          />
          <TextField
            label="Conteúdo"
            fullWidth
            margin="dense"
            multiline
            minRows={3}
            value={newExtraInfo.value}
            onChange={(e) => setNewExtraInfo({ ...newExtraInfo, value: e.target.value })}
          />
          <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
            <Button
              onClick={() => {
                setExtraInfoModalOpen(false);
                setNewExtraInfo({ name: "", value: "" });
                setSelectedExtraInfoIndex(null);
              }}
              variant="outlined"
            >
              Cancelar
            </Button>

            <Button onClick={handleAddExtraInfo} variant="contained" color="primary">
              Salvar
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
      >
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir a informação extra "<strong>{extraInfoToDelete?.name}</strong>"?
          </Typography>
          <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
            <Button onClick={() => setConfirmDeleteOpen(false)} variant="outlined">
              Cancelar
            </Button>
            <Button onClick={handleDeleteExtraInfo} variant="contained" color="error">
              Excluir
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Alteração de Carteira */}
      <Dialog
        open={confirmWalletChangeOpen}
        onClose={handleCancelWalletChange}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningIcon sx={{ color: "#ff9800" }} />
          Confirmar Alteração de Carteira
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
              Você está alterando a carteira de:
            </Typography>
            <Box sx={{ 
              bgcolor: "#f5f5f5", 
              p: 1.5, 
              borderRadius: 1, 
              display: "flex", 
              alignItems: "center",
              gap: 1,
              mb: 2
            }}>
              <ContactIcon sx={{ color: "#666" }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {fullContact.name || "Contato sem nome"}
                </Typography>
                <Typography variant="caption" sx={{ color: "#666" }}>
                  {formattedPhone}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
              📋 Alteração:
            </Typography>
            <Box sx={{ 
              bgcolor: "#fff3e0", 
              p: 1.5, 
              borderRadius: 1, 
              border: "1px solid #ffcc02"
            }}>
              <Typography variant="body2">
                <strong>De:</strong> {pendingWalletChange?.currentUser?.name || "Nenhum atendente"}
              </Typography>
              <Typography variant="body2">
                <strong>Para:</strong> {pendingWalletChange?.newUser?.name || "Nenhum atendente"}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ 
            bgcolor: "#ffebee", 
            p: 1.5, 
            borderRadius: 1, 
            border: "1px solid #f44336"
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: "#d32f2f" }}>
              ⚠️ Atenção:
            </Typography>
            {(() => {
              const isRemovingOwnWallet = pendingWalletChange?.currentUser?.id === user.id && !pendingWalletChange?.userId;
              const isChangingOwnWallet = pendingWalletChange?.currentUser?.id === user.id && pendingWalletChange?.userId;
              const isAdmin = user.profile === "admin" || user.profile === "super";
              
              if (isRemovingOwnWallet) {
                return (
                  <Typography variant="body2" sx={{ color: "#d32f2f" }}>
                    • Você perderá acesso a este contato<br/>
                    • Apenas um administrador poderá vincular novamente
                  </Typography>
                );
              }
              
              if (isChangingOwnWallet) {
                return (
                  <Typography variant="body2" sx={{ color: "#d32f2f" }}>
                    • Você perderá acesso a este contato<br/>
                    • Apenas {pendingWalletChange?.newUser?.name} ou um administrador poderão alterar novamente
                  </Typography>
                );
              }
              
              if (isAdmin && pendingWalletChange?.currentUser) {
                return (
                  <Typography variant="body2" sx={{ color: "#d32f2f" }}>
                    • O usuário {pendingWalletChange?.currentUser?.name} perderá acesso a este contato<br/>
                    • {pendingWalletChange?.userId ? `Apenas ${pendingWalletChange?.newUser?.name} ou um administrador` : "Apenas um administrador"} poderá alterar novamente
                  </Typography>
                );
              }
              
              return (
                <Typography variant="body2" sx={{ color: "#d32f2f" }}>
                  • Esta ação alterará o responsável pelo contato<br/>
                  • {pendingWalletChange?.userId ? `Apenas ${pendingWalletChange?.newUser?.name} ou um administrador` : "Apenas um administrador"} poderá alterar novamente
                </Typography>
              );
            })()}
          </Box>
        </DialogContent>
        <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button 
            onClick={handleCancelWalletChange} 
            variant="outlined"
            sx={{ minWidth: 100 }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmWalletChange} 
            variant="contained" 
            color="warning"
            sx={{ minWidth: 100 }}
          >
            Confirmar
          </Button>
        </Box>
      </Dialog>

    </Drawer>
  );
};

export default ContactDrawer;
